/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Created by Claude Code 2026-09-21: Phase 4 Sidebar Anchors
 * Layout utilities for anchored apps in sidebar during presentations
 */

import type { App } from '@sage3/applications/schema';

/**
 * Represents a positioned app in the sidebar
 */
export interface SidebarLayoutItem {
  appId: string;
  app: App;
  position: { x: number; y: number }; // Position within sidebar (viewport pixels)
  size: { width: number; height: number }; // Size in sidebar (viewport pixels)
}

/**
 * Configuration for sidebar layout
 */
export interface SidebarLayoutConfig {
  sidebarWidth: number; // Width in viewport pixels
  sidebarHeight: number; // Height in viewport pixels
  spacing: number; // Gap between apps in viewport pixels (default: 20)
  minAppHeight: number; // Minimum height per app in viewport pixels (default: 100)
}

/**
 * Auto-layout anchored apps vertically in sidebar
 *
 * Algorithm:
 * - Stack apps vertically with equal spacing
 * - Each app gets equal height (available height / number of apps)
 * - Respect minimum height per app
 * - Add spacing between apps and at edges
 *
 * @param apps - Array of anchored apps to layout
 * @param config - Layout configuration
 * @returns Array of positioned apps ready for rendering
 */
export function layoutAnchoredApps(
  apps: App[],
  config: SidebarLayoutConfig
): SidebarLayoutItem[] {
  const { sidebarWidth, sidebarHeight, spacing = 20, minAppHeight = 100 } = config;

  const totalApps = apps.length;

  // Empty sidebar
  if (totalApps === 0) {
    return [];
  }

  // Calculate available height after spacing
  const totalSpacing = spacing * (totalApps + 1); // Top + between apps + bottom
  const availableHeight = sidebarHeight - totalSpacing;

  // Calculate height per app (equal distribution)
  let appHeight = availableHeight / totalApps;

  // Respect minimum height (apps might overlap if too many)
  if (appHeight < minAppHeight) {
    appHeight = minAppHeight;
    console.warn(
      `Sidebar: ${totalApps} anchored apps exceed available space. ` +
      `Consider limiting to ${Math.floor(availableHeight / minAppHeight)} apps for optimal layout.`
    );
  }

  // Calculate app width (sidebar width minus side spacing)
  const appWidth = sidebarWidth - (spacing * 2);

  // Layout apps vertically
  return apps.map((app, index) => ({
    appId: app._id,
    app,
    position: {
      x: spacing, // Left edge with spacing
      y: spacing + (index * (appHeight + spacing)) // Stack vertically
    },
    size: {
      width: appWidth,
      height: appHeight
    }
  }));
}

/**
 * Convert percentage-based sidebar width to board units
 *
 * @param percentWidth - Sidebar width as percentage (e.g., 15 for 15%)
 * @param viewportWidth - Current viewport width in board units
 * @returns Sidebar width in board units
 */
export function sidebarWidthInBoardUnits(
  percentWidth: number,
  viewportWidth: number
): number {
  return (percentWidth / 100) * viewportWidth;
}

/**
 * Check if anchored apps count is within recommended limits
 *
 * @param count - Number of anchored apps
 * @returns Warning message if too many, null if OK
 */
export function checkAnchorLimit(count: number): string | null {
  const RECOMMENDED_MAX = 5;
  const ABSOLUTE_MAX = 8;

  if (count > ABSOLUTE_MAX) {
    return `Too many anchored apps (${count}). Maximum ${ABSOLUTE_MAX} supported. Sidebar may not render correctly.`;
  }

  if (count > RECOMMENDED_MAX) {
    return `${count} anchored apps may be cramped. Consider limiting to ${RECOMMENDED_MAX} for better readability.`;
  }

  return null;
}
